import { HR_RECORD_TYPES } from '../permissions/modules';
import { counted } from '../composites/countedUnit';

/**
 * Audit retention (piece 9; decisions 0024, 0057): the class is written at
 * insert from the entity and the live requiresCover answer, and the aging pass
 * reads nothing else. Financial and HR events retain indefinitely; a read on a
 * covered account retains indefinitely; a routine read ages out after the
 * window, 36 months until Organization declares the setting.
 */
export type RetentionClassValue = 'Financial' | 'HR' | 'RoutineRead' | 'CoveredRead' | 'Retained';

export const FINANCIAL_ENTITY_TYPES: readonly string[] = [
  'Invoice', 'Bill', 'Payment', 'JournalEntry', 'CreditNote', 'VendorCredit', 'ExpenseReport', 'BankTransaction', 'Commission', 'TaxRegistration', 'StockRevaluation', 'Quote', 'SalesOrder', 'PurchaseOrder',
];

export interface RetentionFacts {
  kind: 'read' | 'write';
  entityType: string;
  /** requiresCover on the record's account at the moment of the read (decision 0057, Amendment). */
  requiresCover?: boolean;
}

export function retentionClassFor(facts: RetentionFacts): RetentionClassValue {
  if ((HR_RECORD_TYPES as readonly string[]).includes(facts.entityType) || facts.entityType === 'DisciplinaryRecord') return 'HR';
  if (FINANCIAL_ENTITY_TYPES.includes(facts.entityType)) return 'Financial';
  if (facts.kind === 'read') return facts.requiresCover ? 'CoveredRead' : 'RoutineRead';
  return 'Retained';
}

export const DEFAULT_AUDIT_READ_RETENTION_MONTHS = 36;

/** The only class the aging pass ever removes. */
export const AGED_CLASS: RetentionClassValue = 'RoutineRead';

export function agingCutoff(now: Date, months: number = DEFAULT_AUDIT_READ_RETENTION_MONTHS): Date {
  const d = new Date(now.getTime());
  d.setUTCMonth(d.getUTCMonth() - months);
  return d;
}

/** Lowering the window destroys data already collected, so the confirmation names the rows about to go and how many. */
export function retentionLoweringConfirmation(facts: { fromMonths: number; toMonths: number; rowsDue: number }): string {
  return `Lowering the routine-read window from ${facts.fromMonths} to ${facts.toMonths} months deletes ${counted(facts.rowsDue, 'audit row')} already collected. This cannot be undone.`;
}

export function isLowering(fromMonths: number, toMonths: number): boolean {
  return toMonths < fromMonths;
}

/** The actions this piece writes; later pieces add theirs beside them. */
export const AUDIT_ACTIONS = {
  // Piece 34, decision 1468: the complaint family; none of the four carries the allegation.
  complaintRaised: 'hr.complaint.raised',
  complaintInvestigationTaken: 'hr.complaint.investigationTaken',
  complaintClosed: 'hr.complaint.closed',
  complaintRouteOffered: 'hr.complaint.routeOffered',
  // Piece 31, decision 0921: a run of an importer block, named with the person and the block and never a credential; its cancel; and a unit match answered.
  importRunStarted: 'import.run.started',
  importRunCancelled: 'import.run.cancelled',
  unitAliasMatched: 'import.unitAlias.matched',
  // Decision 1760: a person marked the comparison of the documents range last written as checked, on that run, with the range.
  importReportsCompared: 'import.reportsCompared',
  // Decision 13070: a Compare Reports download of a run, or of one record of it, on the run, naming the record where one is chosen.
  importCompareReportsDownloaded: 'import.compareReports.downloaded',
  // Decision 1055: an edit to an import mapping row, with each field's value before and after.
  importMappingEdited: 'import.mapping.edited',
  // Decision 1055: a stepped run closed by a person, which frees its block and writes its diff.
  importRunClosed: 'import.run.closed',
  // Decision 12660: one step of a manufacturer lookup accepted on an Item, naming the run and the lines taken.
  itemLookupAccepted: 'catalog.lookup.accepted',
  // Decision 2000: a person acknowledged the Item import's Uncategorized report, on the report's File, with the run that wrote it.
  importUncategorizedAcknowledged: 'import.uncategorizedReport.acknowledged',
  // Decision 1465: Zoho Books connected through Connect with Zoho, and disconnected, each with the person and the time and never a token.
  zohoBooksConnected: 'import.zohoBooks.connected',
  zohoBooksDisconnected: 'import.zohoBooks.disconnected',
  // Decision 8380: Connect with Zoho CRM, its Disconnect, and the manufacturers module a person chose.
  zohoCrmConnected: 'import.zohoCrm.connected',
  zohoCrmDisconnected: 'import.zohoCrm.disconnected',
  zohoCrmModuleChosen: 'import.zohoCrm.moduleChosen',
  // Decision 12720: Connect and Disconnect of Zoho Expense, beside the CRM's.
  zohoExpenseConnected: 'import.zohoExpense.connected',
  zohoExpenseDisconnected: 'import.zohoExpense.disconnected',
  // Decision 3482: the go-live import's opening taken back on a person's act, with the reason, the entry, its reversal and the go-live date it ended.
  importOpeningReversed: 'import.opening.reversed',
  // Decision 9660: Catch Up Cost of Goods ran, on the Organization, naming the invoices, the lines, the total moved and the lines shifted out of Closed periods.
  importCogsCatchUpRan: 'import.cogsCatchUp.ran',
  // Decision 10061: an earlier settled mark on an invoice cleared by the first run after T3483, one event per mark.
  importCogsCatchUpMarkCleared: 'import.cogsCatchUp.markCleared',
  // Decision 10250: Post Inventory Adjustments ran, on the Organization, naming the adjustments, the lines, the net value onto 1200 and those moved out of Closed periods.
  importAdjustmentCatchUpRan: 'import.adjustmentCatchUp.ran',
  // Decision 10470: Start the Documents Again ran, on the Organization, naming each kind's count removed and the backup run taken first.
  importDocumentsStartedAgain: 'import.documents.startedAgain',
  // Piece 21, decision 0971: a change-of-data request filed by an outside login, and its one decision.
  changeRequestFiled: 'changeRequest.filed',
  changeRequestAccepted: 'changeRequest.accepted',
  changeRequestDeclined: 'changeRequest.declined',
  fileRead: 'file.read',
  // Piece 35, decision 0085: an iOS screenshot of a screen drawing a Confidential file, on that file's access trail, in the piece's own word.
  fileScreenshot: 'FileScreenshot',
  fileUploaded: 'file.uploaded',
  /** Decision 7420: a file's stored bytes were encrypted, at upload or by the pass that encrypts plain ones. */
  fileEncrypted: 'file.encrypted',
  fileVerified: 'file.verified',
  fileBlocked: 'file.scan.blocked',
  fileOverride: 'file.blockedExtension.override',
  // Decision 1181: a super admin's Release of a quarantined version, with the reason typed.
  fileQuarantineReleased: 'file.quarantine.released',
  filePurged: 'file.purged',
  shareCreated: 'file.share.created',
  shareRevoked: 'file.share.revoked',
  shareDelivered: 'file.share.delivered',
  shareAccessRequested: 'file.share.accessRequested',
  releaseCreated: 'file.release.created',
  releaseRevoked: 'file.release.revoked',
  /** Decision 8960: a Confidential file's release approved or rejected, written on the record the file sits on so its History says so. */
  releaseApproved: 'file.release.approved',
  releaseRejected: 'file.release.rejected',
  ndaUploaded: 'nda.uploaded',
  ndaActivated: 'nda.activated',
  ndaRevoked: 'nda.revoked',
  ndaObligationEnded: 'nda.obligationEnded',
  ndaRecipientsChanged: 'nda.permittedRecipients.changed',
  verdictCreated: 'screening.verdict.created',
  verdictEdited: 'screening.verdict.edited',
  verdictRetired: 'screening.verdict.retired',
  retentionChanged: 'audit.retention.changed',
  reminderSent: 'reminder.sent',
  // Decision 1081: an event's reminder whose every attempt failed, stamped handled with the failure named.
  eventReminderAbandoned: 'eventReminder.abandoned',
  jobRan: 'job.ran',
  // Decisions 4060, 4063 (T2394): a recovery finished after its restart, an undo of one, and a database it set aside dropped.
  recovered: 'backup.recovered',
  recoverUndone: 'backup.recover.undone',
  recoverDropped: 'backup.recover.dropped',
  // Decision 4180 (T2411): one backup alert per kind per night, its correlation id the kind and the night.
  backupAlerted: 'backup.alerted',
  // Decision 4360 (T2432): a dump key pair made on the page, and a dump refused because its signature did not match.
  dumpKeyGenerated: 'backup.dumpKeyGenerated',
  dumpRefused: 'backup.dumpRefused',
  installCompleted: 'install.completed',
  // Piece 13: the Person page's reveal is an audited read, and the review
  // queue's resolve retires a building without a RecordMerge row to show for it.
  hrDetailsRevealed: 'person.hrDetails.revealed',
  locationResolved: 'location.review.resolved',
  // Decision 7400 (T3078): a suite part taken off a building's address lines and held as its one site's Suite, on both records.
  addressSuiteMoved: 'location.suite.moved',
  // Piece 13: every custom-field write lands on the record's own trail, one entry per attribute name.
  customFieldChanged: 'record.customField.changed',
  // Decision 1491: the values a merge wrote to the survivor from the record merged into it, each with its value before.
  mergeFieldsTaken: 'record.merge.fieldsTaken',
  // Decision 7470 (T3094): an Item merged into the one that stays, on the survivor, naming every duplicate row the merge dropped or ended.
  itemMerged: 'record.merge.item',
  // Decision 7500 (T3098): a merge undone, on the record that came back and on the survivor, the sentence in the reason.
  mergeUndone: 'record.merge.undone',
  // Decision 9040 (T2366): a change of an asset node's parent, from the parent before to the parent after, which Asset Hierarchy Logs reads.
  assetHierarchyMoved: 'asset.hierarchy.moved',
  // Decision 12561 (T3808): an arrow drawn or removed on the plant board, and a card's place kept, each on the node.
  assetFlowLinked: 'asset.flow.linked',
  assetFlowUnlinked: 'asset.flow.unlinked',
  assetBoardPlaced: 'asset.board.placed',
  // Decision 12832 (T3849): a port made or removed on a Function, on the Function.
  assetPortAdded: 'asset.port.added',
  assetPortRemoved: 'asset.port.removed',
  // Decision 12833 (T3849): a Function's parts put in working order on its List, on the Function.
  assetPartsOrdered: 'asset.parts.ordered',
  // Decision 12503 (T3802): the Site page's Plant Feed tile's four writes, on the site.
  plantFeedConnected: 'plantFeed.connected',
  plantFeedRevoked: 'plantFeed.revoked',
  plantFeedTagMapped: 'plantFeed.tagMapped',
  plantFeedTagUnmapped: 'plantFeed.tagUnmapped',
  // Decision 7501 (T3102): an ownership added or ended, on the owned company and on its owner, with both names, the percent and the day.
  ownershipAdded: 'company.ownership.added',
  ownershipEnded: 'company.ownership.ended',
  // Decision 7690 (T3141): a company set Bought Out, on it, and the company that took it over, on the successor, with both names, the day and the person.
  companyBoughtOut: 'company.boughtOut',
  companyTookOver: 'company.tookOver',
  // Decision 7710 (T3140): a person deleted, on them, the reason naming what the delete ended and what it left.
  personDeleted: 'person.deleted',
  // Decision 12700 (T3820): a Deal made by mistake deleted, on the Deal and on its Company, the reason naming the Deal and what went with it.
  dealDeleted: 'deal.deleted',
  // Decision 12700 (T3820): a Work Order made by mistake deleted, on the job and on its Company, the reason naming the job and what went with it.
  workOrderDeleted: 'workOrder.deleted',
  // Decision 12970 (T3867): a visit deleted anyway, on its job, the reason naming the visit and what went with it.
  visitDeleted: 'workOrder.visit.deleted',
  // Decision 12970: a held record's purge, on the record, once its held rows are gone for good.
  recordPurged: 'trash.record.purged',
  // Decision 1604: one record's field changed by a bulk edit over a list's selection, with its value before and after.
  bulkEdited: 'record.bulkEdit.changed',
  // T728, decision 1500: a link deleted by mistake, on the record it hung from, with the typed reason and what the row said.
  linkDeleted: 'record.link.deleted',
  // Decision 2532 (T1133): a Not Shipped package deleted by mistake, on the Sales Order it was packed against, with its number.
  packageDeleted: 'package.deleted',
  // Decision 7891 (T3181): a certificate placed on the wrong site, removed or moved, on the site it left and the one it reached.
  certificateRemoved: 'certificate.removed',
  certificateMoved: 'certificate.moved',
  // Decision 1920: two site rows of one company at one address folded into one, on the company, with the sentence of what went where.
  siteFolded: 'record.site.folded',
  // Decision 5180: a Lead or a Person created from a texted card, on the record created, naming the number and the text.
  textedCardTaken: 'lead.textedCard.taken',
  // Decision 7700 (T3143): a person's name written in title case where it was stored in capitals, on the person, with each field before and after.
  personNameCorrected: 'person.name.corrected',
  // Piece 14: every break-glass authentication is audited, and so is each ordinary sign-in, sign-out, enrolment and password change.
  breakGlassSignedIn: 'auth.breakGlass.signedIn',
  signedIn: 'auth.signedIn',
  signedOut: 'auth.signedOut',
  sessionsRevoked: 'auth.sessions.revoked',
  mfaEnrolled: 'auth.mfa.enrolled',
  passwordChanged: 'auth.password.changed',
  // Piece 21: every invitation, activation, reset, unlock and MFA reset is an audited identity decision.
  invitationIssued: 'auth.invitation.issued',
  invitationRedeemed: 'auth.invitation.redeemed',
  loginActivated: 'auth.login.activated',
  resetRequested: 'auth.reset.requested',
  lockoutCleared: 'auth.lockout.cleared',
  mfaReset: 'auth.mfa.reset',
  recoveryCodeUsed: 'auth.recoveryCode.used',
  loginDeactivated: 'auth.login.deactivated',
  // Decision 0476: an administrator revokes a login's access and restores it, and moves it to another role.
  loginReactivated: 'auth.login.reactivated',
  employmentRoleChanged: 'employment.systemRole.changed',
  // Decision 0324: a device trusted for the second factor, and every device of a login forgotten.
  deviceTrusted: 'auth.device.trusted',
  devicesForgotten: 'auth.devices.forgotten',
  // Decision 0351: a passkey added from a signed-in session, and one removed by the person or an administrator.
  passkeyAdded: 'auth.passkey.added',
  passkeyRemoved: 'auth.passkey.removed',
  // Decision 4652 (3a): a login's phone revoked and held by an administrator, and a held phone allowed again.
  phoneRevoked: 'auth.login.phoneRevoked',
  phoneAllowed: 'auth.login.phoneAllowed',
  // Decision 0352: a sign-in through the identity provider, and a provider's identity refused against a known login.
  ssoSignedIn: 'auth.sso.signedIn',
  ssoRefused: 'auth.sso.refused',
  trashRestored: 'trash.restored',
  // Decision 0679: a certificate request link sent, revoked, and every upload through it, taken or refused.
  certificateRequestSent: 'certificate.request.sent',
  certificateRequestRevoked: 'certificate.request.revoked',
  certificateRequestUploaded: 'certificate.request.uploaded',
  certificateRequestUploadRefused: 'certificate.request.uploadRefused',
  // Piece 21: a record read that only the per-module override permitted is logged, so broad visibility stays accountable.
  overrideRead: 'permission.override.read',
  // Piece 15: the release settings and the disclosure rows are audited on change, and a workweek change is an event of its own.
  settingChanged: 'settings.value.changed',
  // Decision 1250: a punch refused at a clock for its credential, one row per refusal, which the clock's lockout counts.
  clockPunchRefused: 'clock.punch.refused',
  // Decision 1607: a clock device retired on Settings › HR › Work Calendar, naming who retired it; the row's occurrence is when.
  clockDeviceRetired: 'clock.device.retired',
  // T2284: a clock credential issued or revoked on the Employee page, naming the HR user; neither carries the PIN or the badge number.
  clockCredentialIssued: 'clock.credential.issued',
  clockCredentialRevoked: 'clock.credential.revoked',
  // Decision 3781: a pay change entered on the Employee page, and every read of the Pay section, whoever reads it; neither names the rate.
  compensationChanged: 'hr.compensation.changed',
  compensationRead: 'hr.compensation.read',
  // Decision 13130: a job enrolled in a time-off plan with its opening balance, an enrollment ended, and a plan edited on Settings › HR.
  timeOffEnrolled: 'hr.timeOff.enrolled',
  timeOffEnrollmentEnded: 'hr.timeOff.enrollmentEnded',
  timeOffPlanEdited: 'hr.timeOff.planEdited',
  // Decision 13251: HR canceled the unused days of an approved request that had begun, with the reason it gave.
  timeOffCanceledByHr: 'hr.timeOff.canceledByHr',
  // Decision 1390: a holiday added to, or removed from, the table the billing engine reads, with the reason a removal gives.
  holidayAdded: 'holiday.added',
  holidayRemoved: 'holiday.removed',
  // Decision 1252: a role made or edited in Settings, with each column that moved.
  roleCreated: 'systemRole.created',
  roleEdited: 'systemRole.edited',
  // Decision 1195: the recovery passphrase set or changed, and the sealed copy printed for the safe; neither names the passphrase.
  recoveryPassphraseSet: 'keys.recoveryPassphrase.set',
  recoveryCopyPrinted: 'keys.recoveryCopy.printed',
  itemRepriced: 'catalog.item.repriced',
  itemRepricingOneOff: 'catalog.item.repricingOneOff',
  priceListAccepted: 'catalog.priceList.accepted',
  itemAccountsChanged: 'catalog.item.accountsChanged',
  categoryMoved: 'catalog.category.moved',
  disclosureChanged: 'settings.disclosure.changed',
  workweekChanged: 'settings.workweek.changed',
  // Piece 22: Void takes a reason and writes this row; the record and its number stay (decision 0197).
  documentVoided: 'document.voided',
  // Piece 22, decision 0681: an administrator's date set more than thirty days back, with the day it moved from and to.
  documentBackdated: 'document.backdated',
  // Piece 24: the number a check takes at print, which is the moment the series moves; a void writes documentVoided beside it.
  checkPrinted: 'check.printed',
  // Piece 22: each routing, approval and rejection is a row; a rejection is retained, never overwritten, and who approved in what capacity is the audit line (decision 0092).
  documentApprovalRouted: 'document.approvalRouted',
  // T722, decision 2540: the mail a routed document sent each approver, and each reminder while it waits, naming who was and was not mailed.
  documentApprovalMailed: 'document.approvalMailed',
  documentApprovalReminderMailed: 'document.approvalReminderMailed',
  // T1690: a report a failed route put back to Draft, the row that reverses the Draft-to-Submitted row beside it.
  expenseReportRestored: 'expense.report.restored',
  documentApproved: 'document.approved',
  documentRejected: 'document.rejected',
  // Piece 22, decision 1032: the customer purchase order requirement waived on a Sales Order, carrying the reason and who authorised it.
  salesOrderCustomerPoWaived: 'salesOrder.customerPoWaived',
  // Piece 22, Settings › Accounting: a series edit changes the identity of every document after it, a rule or tier edit changes who signs, and an employment's tier is an HR action (pieces 8 and 15).
  numberSeriesChanged: 'settings.numberSeries.changed',
  approvalRuleChanged: 'approvals.rule.changed',
  approvalTierChanged: 'approvals.tier.changed',
  employmentTierChanged: 'employment.approvalTier.changed',
  // Piece 10, decision 0079: a move opens a new dated position and a head change re-points every line derived from the old head, so both change who approves.
  employmentMoved: 'employment.position.moved',
  // T726, decision 1495: a worker's level set from the Person page's Employments row, on the person so their History carries it.
  employmentLevelSet: 'employment.position.levelSet',
  departmentHeadChanged: 'department.head.changed',
  // T3433, piece 8: a confidentiality role granted or ended is written on the person, so the access review and the Person page's History read it.
  confidentialityRoleGranted: 'confidentiality.role.granted',
  confidentialityRoleEnded: 'confidentiality.role.ended',
  // Piece 22: a price sheet is created and superseded, never edited in place; both are rows so the negotiated price has a history.
  priceSheetCreated: 'pricing.sheet.created',
  priceSheetSuperseded: 'pricing.sheet.superseded',
  // Piece 22: the Quote's own moves — sent to the customer, their answer recorded, a sent revision reopened as the next, a Duplicate made.
  documentSent: 'document.sent',
  // Piece 22, decision 0436: the document a person sent by hand and recorded here; never the same row as one this system emailed.
  documentMarkedSent: 'document.markedSent',
  // Piece 22, decision 1696 (T928): a Quote's create, its saved edits naming each field moved, and a floor acknowledgement with its reason.
  documentCreated: 'document.created',
  documentEdited: 'document.edited',
  documentFloorAcknowledged: 'document.floorAcknowledged',
  // Piece 22, decision 1072: a received Purchase Order closed by a person, the last forward move of its one status.
  documentClosed: 'document.closed',
  // Piece 22, decision 1073: an administrator received a Purchase Order the vendor was never sent, with the reason.
  purchaseOrderReceivedUnsent: 'purchaseOrder.receivedUnsent',
  documentAccepted: 'document.accepted',
  // Piece 22, decision 7281 (T1390): a note's held credit applied to an invoice, and paid back to the customer; each carries a reason naming the note.
  paymentHeldCreditApplied: 'payment.heldCreditApplied',
  paymentHeldCreditRefunded: 'payment.heldCreditRefunded',
  documentDeclined: 'document.declined',
  documentReopened: 'document.reopened',
  documentDuplicated: 'document.duplicated',
  // Piece 22, decision 0469: a document said afterwards which document it came from, or stopped saying it.
  // Its own row, never document.sent: attaching an order to an invoice is not sending anything.
  documentSourceLinked: 'document.sourceLinked',
  // Piece 22, decision 10310 (T884): on an approved Bill, an order edit that moved a line it bills, and a receipt that left the line under what approved Bills bill.
  billPurchaseOrderChanged: 'bill.purchaseOrderChanged',
  billReceivedShort: 'bill.receivedShort',
  // Piece 22: the accepted revision converted to a Sales Order, on the Quote it came from.
  documentConverted: 'document.converted',
  // Piece 22: a stock document reaching the state where it writes movements — a transfer received, a count posted, a revaluation written.
  documentPosted: 'document.posted',
  // Piece 22: the Work Order's own rows — every status write, the dispatch that snapshots travel, the overrides with their reasons, a stopped line's re-classification, a follow-up and a reschedule.
  workOrderStatusChanged: 'workOrder.statusChanged',
  workOrderDispatched: 'workOrder.dispatched',
  workOrderTravelOverridden: 'workOrder.travelOverridden',
  workOrderAfterHoursOverridden: 'workOrder.afterHoursOverridden',
  workOrderLineStopped: 'workOrder.lineStopped',
  workOrderFollowUpCreated: 'workOrder.followUpCreated',
  workOrderRescheduled: 'workOrder.rescheduled',
  // Piece 29 (T389, T1300): a job put On Hold and resumed, the reason named on each row.
  workOrderHeld: 'workOrder.held',
  workOrderResumed: 'workOrder.resumed',
  // Piece 29, decision 3071 (T2182): a worker below the line's level assigned from the Dispatch Board, naming the worker, the block and the typed reason.
  workOrderAssignedPastBlock: 'workOrder.assignedPastBlock',
  // Piece 29, decision 1982 (T1244): the technician's own taps on a visit, each one kept, a second tap included; On My Way's row carries the minutes.
  visitOnMyWay: 'workOrderVisit.onMyWay',
  visitArrived: 'workOrderVisit.arrived',
  visitLeft: 'workOrderVisit.left',
  // Piece 29, decisions 1910 and 2061 (T1243, T1263): the lead's Complete the Visit, which freezes the service report, and each Send of it.
  visitCompleted: 'workOrderVisit.completed',
  // Piece 29 (T389): a checklist answer cleared on the job, the value it held named on the Work Order's history.
  checklistAnswerCleared: 'workOrder.checklistAnswerCleared',
  // Decision 2111 (T889): a manager's sign-off on an item left unanswered, the item and the reason named on the Work Order's history.
  checklistItemSignedOff: 'workOrder.checklistItemSignedOff',
  visitReportSent: 'workOrderVisit.reportSent',
  // Piece 29, decision 2080 (T1264): each customer message a visit sent, naming the kind, the addresses and each text's status; and each change of a customer's or a Deal's rule.
  visitMessageSent: 'workOrderVisit.messageSent',
  jobMessageRuleSet: 'jobMessageRule.set',
  jobMessageRuleCleared: 'jobMessageRule.cleared',
  // Piece 29, decision 2081 (T1049): the desk's Dispatch on a visit's block in the Schedule view, which stamps it Dispatched.
  visitDispatched: 'workOrderVisit.dispatched',
  // Piece 29, decision 2250 (T1570): a block dragged on the Schedule view, its window and crew from and to on the row.
  visitMoved: 'workOrderVisit.moved',
  // Piece 29, decision 2813 (T2048): Edit Visit on the job or the visit's page, its window, crew and lead from and to on the row.
  visitEdited: 'workOrderVisit.edited',
  // Piece 29, decision 1983 (T1200): an approver's decision on a submitted day, one row per entry; a rejection's row carries the reason the worker reads.
  timeEntryApproved: 'timeEntry.approved',
  timeEntryRejected: 'timeEntry.rejected',
  // Piece 23: a Manual Journal's post and its reversal, a period's close and reopen, and a manual release of a stock claim.
  journalPosted: 'journal.posted',
  journalReversed: 'journal.reversed',
  periodClosed: 'period.closed',
  periodReopened: 'period.reopened',
  allocationReleased: 'allocation.released',
  // Piece 23, decision 0407: a ledger-drift row is resolved by a person with a reason, and never by a job.
  driftResolved: 'ledger.driftResolved',
  // Piece 25: the tax tables a person maintains and the acts on a registration, a return and a nexus crossing.
  taxRateChanged: 'tax.rate.changed',
  taxRuleChanged: 'tax.categoryRule.changed',
  taxRegistrationOpened: 'tax.registration.opened',
  taxRegistrationClosed: 'tax.registration.closed',
  // Decision 1780: a registration's account number, filing frequency or due rule set after it opened.
  taxRegistrationChanged: 'tax.registration.changed',
  taxFilingFiled: 'tax.filing.filed',
  taxFilingAdjusted: 'tax.filing.adjusted',
  // Decision 0506: the remittance that moves a return Filed to Paid.
  taxFilingPaid: 'tax.filing.paid',
  nexusAcknowledged: 'tax.nexus.acknowledged',
  // Decisions 0736, 0737: a local rate row written, a Location's looked-up jurisdiction confirmed, and a typed county held to the official list.
  localTaxRateChanged: 'tax.localRate.changed',
  taxJurisdictionConfirmed: 'tax.jurisdiction.confirmed',
  locationCountyNamed: 'location.county.named',
  // Decision 7460: an Estimated address a person verified by hand, with who and why on the row.
  locationAddressVerifiedByUser: 'location.address.verifiedByUser',
  // Decision 7460: a Draft that charged the state alone read again at its first send, with the old and the new tax on the row.
  documentTaxRefreshed: 'document.tax.refreshed',
  // Decision 0736: the migration's record of an applicable rate it retired, written by SQL and named here so nothing else takes the name.
  locationApplicableRateRetired: 'location.applicableRate.retired',
  // Piece 26: the commission a calculation wrote, the rate table's own changes, and every read of a per-person
  // override, which is peer-invisible and read-audited from the day it exists. The payroll seam's two exchanges
  // are events of their own, a period exported and a period imported; the hire push was retired by decision 3780.
  commissionCalculated: 'commission.calculated',
  // Decision 0714: what a credit note took back from each recipient, written on the credit note.
  commissionReversed: 'commission.reversed',
  // Decisions 1220, 1221: an invoice issued with its commission unpriced, keeping the engine's reason, and priced once a
  // covering rate was approved; each written on the invoice and on its Deal, so both Histories carry it.
  commissionUnpriced: 'commission.unpriced',
  commissionPriced: 'commission.priced',
  commissionRateChanged: 'commission.rate.changed',
  commissionOverrideRead: 'commission.override.read',
  payrollPeriodExported: 'payroll.period.exported',
  payrollPeriodImported: 'payroll.period.imported',
  // Piece 29: a manager's priority override with its reason (decision 0734), the tier editor's close-and-open, an
  // Agreement's save and its schedule's price change, the coverage answer (decision 0735), and the text confirmation
  // sent and answered.
  workOrderPriorityOverridden: 'workOrder.priorityOverridden',
  priorityTierChanged: 'priorityTier.changed',
  agreementSaved: 'agreement.saved',
  agreementScheduleChanged: 'agreement.scheduleChanged',
  agreementCoverageAnswered: 'agreement.coverageAnswered',
  emergencyConfirmationSent: 'workOrder.confirmationSent',
  emergencyReplyRecorded: 'workOrder.replyRecorded',
  // Piece 29 (T390): an on-call roster day saved, and a Swap, which names both places it traded in one event.
  onCallDaySaved: 'onCall.daySaved',
  onCallSwapped: 'onCall.swapped',
  onCallAlertSent: 'onCall.alertSent',
  onCallAcknowledged: 'onCall.acknowledged',
  onCallNobodyOnCall: 'onCall.nobodyOnCall',
  // Decision 4160 (T2406): a standing weekday's two default names saved, naming the weekday and both names.
  onCallStandingSaved: 'onCall.standingSaved',
  // Piece 30: an article's draft saved, published, archived or deleted, a tag added or removed, a category written and
  // a tier granted or revoked, a quiz saved, and a sitting recorded.
  articleSaved: 'article.saved',
  articlePublished: 'article.published',
  articleArchived: 'article.archived',
  articleDeleted: 'article.deleted',
  articleTagged: 'article.tagged',
  articleUntagged: 'article.untagged',
  articleCategorySaved: 'articleCategory.saved',
  articleCategoryGranted: 'articleCategory.granted',
  articleCategoryRevoked: 'articleCategory.revoked',
  quizSaved: 'quiz.saved',
  quizAttempted: 'quiz.attempted',
  // Piece 32: a message reported as spam or phishing, released from Quarantine or its rejection confirmed (decision 0078),
  // tagged to a record or untagged, and a mailbox read through a grant, which the access audit reads.
  mailReported: 'mail.reported',
  mailReleased: 'mail.released',
  mailRejectionConfirmed: 'mail.rejectionConfirmed',
  mailTagged: 'mail.tagged',
  // T3432, decision 9800: a dedicated Documents Inbox address stamped its classification on a message in the accounting mailbox.
  mailInboxClassified: 'mail.inboxClassified',
  mailUntagged: 'mail.untagged',
  mailboxRead: 'mail.mailbox.read',
  // Piece 32 part b: a composed or module message handed to the transport, one it refused, a held send cancelled,
  // and a filter rule written, re-ordered or removed (decisions 0081, 0791).
  mailSent: 'mail.sent',
  mailSendFailed: 'mail.sendFailed',
  mailSendCancelled: 'mail.sendCancelled',
  mailFilterRuleSaved: 'mail.filterRule.saved',
  mailFilterRuleRemoved: 'mail.filterRule.removed',
  mailSignatureSaved: 'mail.signature.saved',
  // Piece 32 part c: a mailbox grant given or ended from the mailbox's record page (decision 0840).
  mailGrantGiven: 'mail.grant.given',
  mailGrantEnded: 'mail.grant.ended',
  // A Department mailbox's name, address or department edited on its record page (decision 0842).
  mailboxEdited: 'mail.mailbox.edited',
  // A Department or a Module mailbox made with + New Mailbox on Settings › Email › Mailboxes (decision 12640).
  mailboxCreated: 'mail.mailbox.created',
  // A mailbox switched between watching and writing to its host, a super admin's act (decision 1206).
  mailboxHostModeChanged: 'mail.mailbox.hostModeChanged',
  // A person's out-of-office saved, a departed mailbox's reply saved, and an automatic reply sent at first sight (decision 0843).
  mailOutOfOfficeSaved: 'mail.outOfOffice.saved',
  mailDepartedReplySaved: 'mail.departedReply.saved',
  mailAutoReplied: 'mail.autoReplied',
  // T2371 (decision 1201): a recipient's S/MIME certificate uploaded, removed, or a discovered one accepted or rejected in Recipient Certificates.
  mailCertificateUploaded: 'mail.certificate.uploaded',
  mailCertificateRemoved: 'mail.certificate.removed',
  mailCertificateAccepted: 'mail.certificate.accepted',
  mailCertificateRejected: 'mail.certificate.rejected',
  // Piece 22 with piece 32 (decision 0162): a Quote or Sales Order moved to Client viewed by the first open of its link.
  documentClientViewed: 'document.clientViewed',
  // Piece 18, decision 1301: every change a person makes to a Deal's related rows, written on the Deal so its History carries it.
  dealContactAdded: 'deal.contact.added',
  dealContactEnded: 'deal.contact.ended',
  dealContactMadePrimary: 'deal.contact.madePrimary',
  dealTeamAdded: 'deal.team.added',
  dealTeamEnded: 'deal.team.ended',
  dealTeamChanged: 'deal.team.changed',
  dealAuthorityAdded: 'deal.authority.added',
  dealAuthorityChanged: 'deal.authority.changed',
  dealAuthorityRemoved: 'deal.authority.removed',
  dealBomAttached: 'deal.bom.attached',
  dealBomDetached: 'deal.bom.detached',
  dealBudgetSaved: 'deal.budget.saved',
  dealTimingSaved: 'deal.timing.saved',
  // Piece 18, decision 1351: the Deal's own field edits and stage moves, written on the Deal beside the change log the version gate reads.
  dealEdited: 'deal.edited',
  dealStageMoved: 'deal.stage.moved',
  dealStageWon: 'deal.stage.won',
  dealStageLost: 'deal.stage.lost',
  dealStageReopened: 'deal.stage.reopened',
  // Piece 18, decision 1482: Clone, written on the Deal cloned from and naming the Deal it made.
  dealCloned: 'deal.cloned',
  // Piece 9, decision 1487: a Deal's creation, written on the new Deal naming the person and, for a clone, the Deal it came from.
  dealCreated: 'deal.created',
  // Piece 19, decision 5400: the Deal Pack made for the viewer, copied or downloaded, written on the Deal as a read.
  dealPackRead: 'deal.pack.read',
  // Piece 18 with piece 9, decision 1466: a save that moved a bill of materials' cost after a Quote drawn from it went out, written on the list.
  bomCostChangedAfterQuote: 'bom.costChangedAfterQuote',
} as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[keyof typeof AUDIT_ACTIONS];

/** 🔒 Decision 13090: `Assignment` is a visit's own crew on its signed sheet. */
export type FileGrantKindLabel = 'Class' | 'Authorship' | 'Release' | 'ShareLink' | 'ConfidentialityRole' | 'Assignment';

/** logFileAccess is the one writer of grantKind, grantPermission and a Denied outcome; this is the row it writes. */
export interface FileAccessAuditRow {
  action: typeof AUDIT_ACTIONS.fileRead;
  entityType: 'File';
  entityId: string;
  outcome: 'Allowed' | 'Denied';
  grantKind: FileGrantKindLabel | null;
  grantPermission: 'View' | 'Download' | null;
  retentionClass: RetentionClassValue;
  reason: string | null;
}

export function fileAccessAuditRow(fileId: string, answer: { allowed: true; grant: { kind: FileGrantKindLabel; permission: 'View' | 'Download' } } | { allowed: false; message: string }, requiresCover: boolean): FileAccessAuditRow {
  const retentionClass = retentionClassFor({ kind: 'read', entityType: 'File', requiresCover });
  if (answer.allowed) return { action: AUDIT_ACTIONS.fileRead, entityType: 'File', entityId: fileId, outcome: 'Allowed', grantKind: answer.grant.kind, grantPermission: answer.grant.permission, retentionClass, reason: null };
  return { action: AUDIT_ACTIONS.fileRead, entityType: 'File', entityId: fileId, outcome: 'Denied', grantKind: null, grantPermission: null, retentionClass, reason: answer.message };
}
